import React, { Suspense, lazy } from 'react'
import { Redirect, Route, Switch } from 'Core'

// Components
import FallBack from 'Components/Fallback'
import CheckFeatureFlag from 'Views/CheckFeatureFlag'
import { lazyWithRetry } from 'Core/lazyWithRetry'
// features/layout is already part of the eager shell (Routes.jsx), so these
// tiny redirect components stay statically imported.
import {
  RedirectJiraSuccess,
  RedirectMSTeamSuccess,
  RedirectServiceNowSuccess,
  RedirectSlackSuccess
} from 'features/layout'

const DeviceTabs = lazyWithRetry(() => import('Views/DeviceTabs'))
const IAMActionDetailsWithContext = lazyWithRetry(
  () => import('Views/IamActionsAndResources/IAMActionList/IAMActionDetails')
)

// Feature Routes — lazy-loaded so each feature ships in its own chunk and is
// fetched only when its route is first visited.
const ActivityLogsRoute = lazyWithRetry(() =>
  import('features/activityLogs').then((m) => ({ default: m.ActivityLogsRoute }))
)
const ApplicationRoutes = lazyWithRetry(() =>
  import('features/applications').then((m) => ({ default: m.ApplicationRoutes }))
)
const ApprovalListRoutes = lazyWithRetry(() =>
  import('features/approvals').then((m) => ({ default: m.ApprovalListRoutes }))
)
const BundlesRoutes = lazyWithRetry(() =>
  import('features/bundles').then((m) => ({ default: m.BundlesRoutes }))
)
const CloudsRoutes = lazyWithRetry(() =>
  import('features/clouds').then((m) => ({ default: m.CloudsRoutes }))
)
const DashboardRoutes = lazyWithRetry(() =>
  import('features/dashboard').then((m) => ({ default: m.DashboardRoutes }))
)
const GatewayRoutes = lazyWithRetry(() =>
  import('features/gateways').then((m) => ({ default: m.GatewayRoutes }))
)
const TeamDetails = lazyWithRetry(() =>
  import('features/github').then((m) => ({ default: m.TeamDetails }))
)
const GroupsRoutes = lazyWithRetry(() =>
  import('features/groups').then((m) => ({ default: m.GroupsRoutes }))
)
const IAMResourcesRoutes = lazyWithRetry(() =>
  import('features/iamResources').then((m) => ({ default: m.IAMResourcesRoutes }))
)
const IAMRolesRoutes = lazyWithRetry(() =>
  import('features/iamRoles').then((m) => ({ default: m.IAMRolesRoutes }))
)
const IdaRoutes = lazyWithRetry(() =>
  import('features/IdentityAnalyzer').then((m) => ({ default: m.IdaRoutes }))
)
const IntegrationsRoutes = lazyWithRetry(() =>
  import('features/integrations').then((m) => ({ default: m.IntegrationsRoutes }))
)
const PolicyRoutes = lazyWithRetry(() =>
  import('features/policy').then((m) => ({ default: m.PolicyRoutes }))
)
const ProxyControllersRoutes = lazyWithRetry(() =>
  import('features/proxyControllers').then((m) => ({ default: m.ProxyControllersRoutes }))
)
const ServerControllersRoutes = lazyWithRetry(() =>
  import('features/serverControllers').then((m) => ({ default: m.ServerControllersRoutes }))
)
const SessionRoutes = lazyWithRetry(() =>
  import('features/sessions').then((m) => ({ default: m.SessionRoutes }))
)
const SettingsRoute = lazyWithRetry(() =>
  import('features/settings').then((m) => ({ default: m.SettingsRoute }))
)
const DevicePassKeys = lazyWithRetry(() =>
  import('features/settings').then((m) => ({ default: m.DevicePassKeys }))
)
const DataStoreRoutes = lazyWithRetry(() =>
  import('features/snowflake').then((m) => ({ default: m.DataStoreRoutes }))
)
const TagsRoutes = lazyWithRetry(() =>
  import('features/tags').then((m) => ({ default: m.TagsRoutes }))
)
const TargetsRoutes = lazyWithRetry(() =>
  import('features/targets').then((m) => ({ default: m.TargetsRoutes }))
)
const ADDomainControllers = lazyWithRetry(() =>
  import('features/targets').then((m) => ({ default: m.ADDomainControllers }))
)
const CreateADD = lazyWithRetry(() =>
  import('features/targets').then((m) => ({ default: m.CreateADD }))
)
const UserRoutes = lazyWithRetry(() =>
  import('features/users').then((m) => ({ default: m.UserRoutes }))
)
const SecretsRoutes = lazyWithRetry(() =>
  import('features/secrets').then((m) => ({ default: m.SecretsRoutes }))
)
const ServiceAccounts = lazyWithRetry(() =>
  import('features/servieAccounts').then((m) => ({ default: m.ServiceAccounts }))
)
const ShadowAI = lazyWithRetry(() =>
  import('features/shadowAI').then((m) => ({ default: m.ShadowAI }))
)
const NHIAgents = lazyWithRetry(() =>
  import('features/shadowAI').then((m) => ({ default: m.NHIAgents }))
)
const NonHumanIdentitiesRoutes = lazyWithRetry(() =>
  import('features/nonHumanIdentities').then((m) => ({ default: m.NonHumanIdentitiesRoutes }))
)
const CloudAgentsRoutes = lazyWithRetry(() =>
  import('features/cloudAgents').then((m) => ({ default: m.CloudAgentsRoutes }))
)
const AgentsRoutes = lazyWithRetry(() =>
  import('features/agents').then((m) => ({ default: m.AgentsRoutes }))
)
const AIPolicyRoutes = lazyWithRetry(() =>
  import('features/aiPolicy').then((m) => ({ default: m.AIPolicyRoutes }))
)
const AgentApprovalRoutes = lazyWithRetry(() =>
  import('features/agentApproval').then((m) => ({ default: m.AgentApprovalRoutes }))
)
const AgentInsightsRoutes = lazyWithRetry(() =>
  import('features/agentInsights').then((m) => ({ default: m.AgentInsightsRoutes }))
)
const AIIntentRoutes = lazyWithRetry(() =>
  import('features/aiIntent').then((m) => ({ default: m.AIIntentRoutes }))
)
const OverviewRoutes = lazyWithRetry(() =>
  import('features/overviewDashboard').then((m) => ({ default: m.OverviewRoutes }))
)

// Lazy Loaded Components
const IAMActionAndResources = lazy(() => import('Views/IamActionsAndResources'))
const DeviceDetails = lazy(() => import('Views/DeviceList/DeviceDetails'))
const NotFound = lazy(() => import('Views/NotFound'))

const VIEW_PREFIX = 'admin'

const AdminviewRoutes = () => {
  return (
    <Suspense fallback={<FallBack />}>
      <Switch>
        {/* Root Redirects */}
        <Route exact path={[`/${VIEW_PREFIX}/`, `/${VIEW_PREFIX}/index.html`]}>
          <Redirect to={`/${VIEW_PREFIX}/dashboard`} />
        </Route>

        {/* Main Feature Routes */}
        <Route
          exact
          path={[`/${VIEW_PREFIX}/passkeys`, `/${VIEW_PREFIX}/passkeys/*`]}
          component={DevicePassKeys}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/gateways`, `/${VIEW_PREFIX}/gateways/*`]}
          component={GatewayRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/proxy-controllers`, `/${VIEW_PREFIX}/proxy-controllers/*`]}
          component={ProxyControllersRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/server-controllers`, `/${VIEW_PREFIX}/server-controllers/*`]}
          component={ServerControllersRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/tags`, `/${VIEW_PREFIX}/tags/*`]}
          component={TagsRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/dashboard`, `/${VIEW_PREFIX}/dashboard/*`]}
          component={DashboardRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/bundles`, `/${VIEW_PREFIX}/bundles/*`]}
          component={BundlesRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/iam-roles`, `/${VIEW_PREFIX}/iam-roles/*`]}
          component={IAMRolesRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/activity-logs`, `/${VIEW_PREFIX}/activity-logs/*`]}
          component={ActivityLogsRoute}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/targets`, `/${VIEW_PREFIX}/targets/*`]}
          component={TargetsRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/resources`, `/${VIEW_PREFIX}/resources/*`]}
          component={IAMResourcesRoutes}
        />
        <Route
          exact
          path={[
            `/${VIEW_PREFIX}/policys`,
            `/${VIEW_PREFIX}/policys/*`,
            // correctly-spelled alias; both render the same PolicyRoutes
            `/${VIEW_PREFIX}/policies`,
            `/${VIEW_PREFIX}/policies/*`
          ]}
          component={PolicyRoutes}
        />
        <Route
          exact
          path={[
            `/${VIEW_PREFIX}/applications`,
            `/${VIEW_PREFIX}/applications/*`,
            `/${VIEW_PREFIX}/saas-apps`,
            `/${VIEW_PREFIX}/saas-apps/*`,
            `/${VIEW_PREFIX}/agentic-apps`,
            `/${VIEW_PREFIX}/agentic-apps/*`
          ]}
          component={ApplicationRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/datacloud`, `/${VIEW_PREFIX}/datacloud/*`]}
          component={DataStoreRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/users`, `/${VIEW_PREFIX}/users/*`]}
          component={UserRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/groups`, `/${VIEW_PREFIX}/groups/*`]}
          component={GroupsRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/access-requests`, `/${VIEW_PREFIX}/access-requests/*`]}
          component={ApprovalListRoutes}
        />
        <Route
          exact
          path={[
            `/${VIEW_PREFIX}/settings`,
            `/${VIEW_PREFIX}/settings/*`,
            `/${VIEW_PREFIX}/settings/:tabName`
          ]}
          component={SettingsRoute}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/approver-policies`, `/${VIEW_PREFIX}/approver-policies/*`]}
          component={SettingsRoute}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/clouds`, `/${VIEW_PREFIX}/clouds/*`]}
          component={CloudsRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/session-logs`, `/${VIEW_PREFIX}/session-logs/*`]}
          component={SessionRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/secrets`, `/${VIEW_PREFIX}/secrets/*`]}
          component={SecretsRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/integrations`, `/${VIEW_PREFIX}/integrations/*`]}
          component={IntegrationsRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/personal-agents`, `/${VIEW_PREFIX}/personal-agents/*`]}
          component={ShadowAI}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/nhi-agents`, `/${VIEW_PREFIX}/nhi-agents/*`]}
          component={NHIAgents}
        />
        <Route
          path={[`/${VIEW_PREFIX}/agent-config`, `/${VIEW_PREFIX}/agent-config/*`]}
          component={AgentsRoutes}
        />
        <Route
          path={[
            `/${VIEW_PREFIX}/non-human-identities`,
            `/${VIEW_PREFIX}/non-human-identities/*`
          ]}
          component={NonHumanIdentitiesRoutes}
        />
        <Route
          path={[`/${VIEW_PREFIX}/ai-policys`, `/${VIEW_PREFIX}/ai-policys/*`]}
          component={AIPolicyRoutes}
        />
        <Route
          path={[`/${VIEW_PREFIX}/agent-approvals`, `/${VIEW_PREFIX}/agent-approvals/*`]}
          component={AgentApprovalRoutes}
        />
        <Route
          path={[`/${VIEW_PREFIX}/agent-insights`, `/${VIEW_PREFIX}/agent-insights/*`]}
          component={AgentInsightsRoutes}
        />
        <Route
          path={[`/${VIEW_PREFIX}/ai-intents`, `/${VIEW_PREFIX}/ai-intents/*`]}
          component={AIIntentRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/workload-agents`, `/${VIEW_PREFIX}/workload-agents/*`]}
          component={CloudAgentsRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/overview`, `/${VIEW_PREFIX}/overview/*`]}
          component={OverviewRoutes}
        />
        <Route
          exact
          path={[`/${VIEW_PREFIX}/idaanalyzer`, `/${VIEW_PREFIX}/idaanalyzer/*`]}
          component={IdaRoutes}
        />

        {/* Active Directory Routes */}
        <Route exact path={`/${VIEW_PREFIX}/addomaincontrollers`} component={ADDomainControllers} />
        <Route
          exact
          path={`/${VIEW_PREFIX}/addomainControllers/:controllername`}
          component={CreateADD}
        />

        {/* IAM Action Routes with Feature Flags */}
        <Route exact path={`/${VIEW_PREFIX}/iam-actions/:accountType`}>
          <CheckFeatureFlag isRoute permission='showIamActions'>
            <IAMActionAndResources tab='iam-actions' />
          </CheckFeatureFlag>
        </Route>

        <Route exact path={`/${VIEW_PREFIX}/iam-policy/:accountType`}>
          <CheckFeatureFlag isRoute permission='showIamActions'>
            <IAMActionAndResources tab='iam-policy' />
          </CheckFeatureFlag>
        </Route>

        <Route exact path={`/${VIEW_PREFIX}/account-resources/:accountType`}>
          <CheckFeatureFlag isRoute permission='showIamActions'>
            <IAMActionAndResources tab='account-resources' />
          </CheckFeatureFlag>
        </Route>

        <Route
          exact
          path={[`/${VIEW_PREFIX}/iam-action/:name`, `/${VIEW_PREFIX}/iam-action/:name/:action`]}
        >
          <CheckFeatureFlag isRoute permission='showIamActions'>
            <IAMActionDetailsWithContext />
          </CheckFeatureFlag>
        </Route>

        {/* Service Accounts Routes */}
        <Route
          exact
          path={[
            `/${VIEW_PREFIX}/service-accounts`,
            `/${VIEW_PREFIX}/service-accounts/:accountType`,
            `/${VIEW_PREFIX}/service-accounts/:accountType/:action`
          ]}
        >
          <CheckFeatureFlag isRoute permission='showServiceAccountsPage'>
            <ServiceAccounts />
          </CheckFeatureFlag>
        </Route>

        {/* Integration Redirect Routes */}
        <Route exact path={`/${VIEW_PREFIX}/slack/:integrationName`}>
          <CheckFeatureFlag isRoute permission='showIntegrationsPage'>
            <RedirectSlackSuccess />
          </CheckFeatureFlag>
        </Route>

        <Route exact path={`/${VIEW_PREFIX}/jira/:integrationName`}>
          <CheckFeatureFlag isRoute permission='showIntegrationsPage'>
            <RedirectJiraSuccess />
          </CheckFeatureFlag>
        </Route>

        <Route exact path={`/${VIEW_PREFIX}/servicenow/:integrationName`}>
          <CheckFeatureFlag isRoute permission='showIntegrationsPage'>
            <RedirectServiceNowSuccess />
          </CheckFeatureFlag>
        </Route>

        <Route exact path={`/${VIEW_PREFIX}/msteams/:integrationName`}>
          <CheckFeatureFlag isRoute permission='showIntegrationsPage'>
            <RedirectMSTeamSuccess />
          </CheckFeatureFlag>
        </Route>

        {/* Device Routes */}
        <Route exact path={`/${VIEW_PREFIX}/devices`}>
          <CheckFeatureFlag isRoute permission='showDevicesPage'>
            <DeviceTabs />
          </CheckFeatureFlag>
        </Route>

        <Route exact path={`/${VIEW_PREFIX}/device/:id`}>
          <CheckFeatureFlag isRoute permission='showDeviceDetailsPage'>
            <DeviceDetails />
          </CheckFeatureFlag>
        </Route>

        {/* Team Details Route */}
        <Route exact path={`/${VIEW_PREFIX}/team-details/:teamName`}>
          <TeamDetails />
        </Route>

        {/* 404 - Must be last */}
        <Route>
          <NotFound />
        </Route>
      </Switch>
    </Suspense>
  )
}

export default AdminviewRoutes
